import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useLocalizedPath } from "../components/Header";
import {
  CheckCircle,
  XCircle,
  FileText,
  Shield,
  ArrowRight,
  Info,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";

export default function ZulassungsfreieTaetigkeiten() {
  const { t } = useTranslation();
  const { getPath } = useLocalizedPath();

  const allowedServices = t('licenseFree.allowed.items', { returnObjects: true }) || [];
  const notOfferedKeys = ['electrical', 'plumbing', 'structural', 'facade', 'gas'];

  return (
    <div data-testid="zulassung-page">
      {/* Hero Section */}
      <section className="bg-[#2D3748] py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold font-['Manrope'] text-white mb-6">
              {t('licenseFree.hero.title')}
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              {t('licenseFree.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24 bg-[#F7FAFC]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Info Alert */}
              <Alert className="bg-blue-50 border-blue-200">
                <Info className="h-5 w-5 text-blue-600" />
                <AlertTitle className="text-blue-800 font-['Manrope']">
                  {t('licenseFree.info.title')}
                </AlertTitle>
                <AlertDescription className="text-blue-700 mt-2">
                  {t('licenseFree.info.description')}
                </AlertDescription>
              </Alert>

              {/* Allowed Services */}
              <Card className="bg-white border" data-testid="allowed-services-card">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 rounded-lg bg-green-100 text-green-600">
                      <CheckCircle className="h-5 w-5" />
                    </div>
                    <h2 className="text-2xl font-semibold font-['Manrope'] text-[#2D3748]">
                      {t('licenseFree.allowed.title')}
                    </h2>
                  </div>
                  <p className="text-gray-600 mb-6">
                    {t('licenseFree.allowed.description')}
                  </p>
                  <ul className="space-y-3">
                    {Array.isArray(allowedServices) && allowedServices.map((service, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0"
                      >
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{service}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* Not Offered Services */}
              <Card className="bg-white border" data-testid="not-offered-services-card">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="p-2 rounded-lg bg-red-100 text-red-600">
                      <XCircle className="h-5 w-5" />
                    </div>
                    <h2 className="text-2xl font-semibold font-['Manrope'] text-[#2D3748]">
                      {t('licenseFree.notOffered.title')}
                    </h2>
                  </div>
                  <p className="text-gray-600 mb-6">
                    {t('licenseFree.notOffered.description')}
                  </p>
                  <div className="space-y-4">
                    {notOfferedKeys.map((key, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 py-3 px-4 bg-gray-50 rounded-lg"
                      >
                        <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <span className="font-medium text-gray-800">
                            {t(`licenseFree.notOffered.items.${key}.title`)}
                          </span>
                          <p className="text-sm text-gray-500 mt-1">
                            {t(`licenseFree.notOffered.items.${key}.reason`)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-6">
              {/* Legal Notice */}
              <Card className="bg-orange-50 border-orange-200">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Shield className="h-6 w-6 text-orange-500" />
                    <h3 className="text-lg font-semibold font-['Manrope'] text-[#2D3748]">
                      {t('licenseFree.sidebar.legalSafety.title')}
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {t('licenseFree.sidebar.legalSafety.description')}
                  </p>
                </CardContent>
              </Card>

              {/* Legal Reference */}
              <Card className="bg-white border">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <FileText className="h-6 w-6 text-[#2D3748]" />
                    <h3 className="text-lg font-semibold font-['Manrope'] text-[#2D3748]">
                      {t('licenseFree.sidebar.legalBasis.title')}
                    </h3>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    {t('licenseFree.sidebar.legalBasis.description')}
                  </p>
                  <a
                    href="https://www.gesetze-im-internet.de/hwo/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-orange-500 hover:text-orange-600 text-sm font-medium flex items-center gap-1"
                  >
                    {t('licenseFree.sidebar.legalBasis.link')}
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </CardContent>
              </Card>

              {/* CTA Card */}
              <Card className="bg-[#2D3748] border-0">
                <CardContent className="p-6 text-center">
                  <h3 className="text-lg font-semibold font-['Manrope'] text-white mb-3">
                    {t('licenseFree.sidebar.cta.title')}
                  </h3>
                  <p className="text-gray-300 text-sm mb-4">
                    {t('licenseFree.sidebar.cta.description')}
                  </p>
                  <Button
                    asChild
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="zulassung-cta-button"
                  >
                    <Link to={getPath('contact')}>
                      {t('licenseFree.sidebar.cta.button')}
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
